import type { jsPDF } from "jspdf";
import type { CellHook } from "jspdf-autotable";
import { PIXELS_TO_POINTS } from "../utils/excelToFortuneSheet";
import { type BorderSide, borderCellKey, type CellBorderSides, FortuneBorderStyle, type FortuneBorderStyleCode, isBorderSide } from "../utils/sheetBorders";

/**
 * Each cell's OWN Excel borders on the PDF. Excel prints no gridlines by default, so a cell without a
 * border gets no line, and a bordered cell gets the weight, pattern and colour it has in the preview.
 * Both renderings read the same `borderInfo` entries.
 *
 * The lines are collected while autoTable draws a band and stroked once the band is finished. Drawn
 * per cell, they would be partly painted over: autoTable fills every cell (white unless the workbook
 * fills it), so the next cell to the right or below would cover half of each shared edge.
 */

/**
 * One stretch of a drawn cell's side: the border (or none) of one sheet column or row the cell spans,
 * with that column's width or row's height as its share of the side. A plain cell has one run per side.
 * A merge has one per column along its top and bottom and one per row along its left and right, because
 * Excel takes each stretch of a merge's outline from the member cell it lies on.
 */
export type EdgeRun = { side: BorderSide | undefined; weightPt: number };

export type CellEdges = { top: EdgeRun[]; bottom: EdgeRun[]; left: EdgeRun[]; right: EdgeRun[] };

/** Where a drawn cell sits on its sheet: its top-left cell and how far it spans. */
type CellSpan = { row: number; column: number; rowSpan: number; colSpan: number };

const hasAnyLine = (edges: CellEdges): boolean => [edges.top, edges.bottom, edges.left, edges.right].some((runs) => runs.some((run) => run.side !== undefined));

/**
 * The outline of the drawn cell covering `span`, or `undefined` when none of it has a line. That lets a
 * printout with no borders skip the drawing hook for every cell.
 *
 * Hidden columns (zero width) are left out of the top and bottom runs, so those runs match the visible
 * columns autoTable lays out, one for one. A side's runs come from the cells on that side of the span,
 * so the borderInfo of a merge's interior cells is never consulted.
 */
export const cellEdgesFor = (
	borders: Map<string, CellBorderSides>,
	span: CellSpan,
	columnWidthsPt: number[],
	rowHeightsPt: number[],
): CellEdges | undefined => {
	const lastRow = span.row + span.rowSpan - 1;
	const lastColumn = span.column + span.colSpan - 1;
	const edges: CellEdges = { top: [], bottom: [], left: [], right: [] };
	for (let c = span.column; c <= lastColumn; c++) {
		const weightPt = columnWidthsPt[c];
		if (weightPt <= 0) continue;
		edges.top.push({ side: borders.get(borderCellKey(span.row, c))?.t, weightPt });
		edges.bottom.push({ side: borders.get(borderCellKey(lastRow, c))?.b, weightPt });
	}
	for (let r = span.row; r <= lastRow; r++) {
		const weightPt = rowHeightsPt[r];
		edges.left.push({ side: borders.get(borderCellKey(r, span.column))?.l, weightPt });
		edges.right.push({ side: borders.get(borderCellKey(r, lastColumn))?.r, weightPt });
	}
	return hasAnyLine(edges) ? edges : undefined;
};

/**
 * The outline of a merge cut at a column band's last column (see `cellsForBand`). Only the first
 * `columnCount` columns of its top and bottom are on this page. Its right edge is not on this page at
 * all, because the merge carries on past the band, so it is dropped rather than drawn at the cut.
 */
export const clipEdgesToColumns = (edges: CellEdges, columnCount: number): CellEdges => ({
	top: edges.top.slice(0, columnCount),
	bottom: edges.bottom.slice(0, columnCount),
	left: edges.left,
	right: [],
});

const isEdgeRuns = (value: unknown): value is EdgeRun[] =>
	Array.isArray(value) &&
	value.every(
		(run: unknown) =>
			typeof run === "object" &&
			run !== null &&
			"weightPt" in run &&
			typeof run.weightPt === "number" &&
			"side" in run &&
			(run.side === undefined || isBorderSide(run.side)),
	);

/** The edges `buildSheetBody` attached, read off the cell definition autoTable stores as `raw` unchanged. */
const cellEdgesOf = (raw: unknown): CellEdges | undefined => {
	if (typeof raw !== "object" || raw === null || !("edges" in raw) || raw.edges === undefined) return undefined;
	const { edges } = raw;
	if (
		typeof edges !== "object" ||
		edges === null ||
		!("top" in edges && "bottom" in edges && "left" in edges && "right" in edges) ||
		!isEdgeRuns(edges.top) ||
		!isEdgeRuns(edges.bottom) ||
		!isEdgeRuns(edges.left) ||
		!isEdgeRuns(edges.right)
	) {
		throw new Error(`Malformed cell edges: ${JSON.stringify(edges)}`);
	}
	return { top: edges.top, bottom: edges.bottom, left: edges.left, right: edges.right };
};

type Orientation = "horizontal" | "vertical";

/** One straight border line. `fixedPt` is its y (horizontal) or x (vertical); it runs from `fromPt` to `toPt`. */
type BorderSegment = { page: number; orientation: Orientation; fixedPt: number; fromPt: number; toPt: number; side: BorderSide };

/** Every border line one column band asked for, held until the band is done (see the module comment). */
export type BorderCollector = BorderSegment[];

type SideGeometry = { page: number; orientation: Orientation; fixedPt: number; startPt: number; lengthPt: number };

/** Lay a side's runs end to end along it, each taking its share of the drawn length. Proportional rather
 *  than absolute, so that a row autoTable split across a page break still gets its outline. */
const pushSide = (collector: BorderCollector, runs: EdgeRun[], { page, orientation, fixedPt, startPt, lengthPt }: SideGeometry): void => {
	const totalWeightPt = runs.reduce((sum, run) => sum + run.weightPt, 0);
	if (totalWeightPt <= 0) return;
	let fromPt = startPt;
	for (const run of runs) {
		const toPt = fromPt + (lengthPt * run.weightPt) / totalWeightPt;
		if (run.side) collector.push({ page, orientation, fixedPt, fromPt, toPt, side: run.side });
		fromPt = toPt;
	}
};

/** A `didDrawCell` hook that records the drawn cell's outline into `collector` at the exact place and on
 *  the page autoTable put the cell, including the copies of repeated title rows on every page. */
export const collectCellBorders =
	(doc: jsPDF, collector: BorderCollector): CellHook =>
	(data) => {
		const edges = cellEdgesOf(data.cell.raw);
		if (!edges) return;
		const { x, y, width, height } = data.cell;
		const page = doc.getCurrentPageInfo().pageNumber;
		pushSide(collector, edges.top, { page, orientation: "horizontal", fixedPt: y, startPt: x, lengthPt: width });
		pushSide(collector, edges.bottom, { page, orientation: "horizontal", fixedPt: y + height, startPt: x, lengthPt: width });
		pushSide(collector, edges.left, { page, orientation: "vertical", fixedPt: x, startPt: y, lengthPt: height });
		pushSide(collector, edges.right, { page, orientation: "vertical", fixedPt: x + width, startPt: y, lengthPt: height });
	};

/**
 * How each style is stroked, in the preview's own canvas pixels: the widths and dash arrays are
 * @fortune-sheet/core's `setLineDash` (Thin 1px, Medium 2px, Thick 3px). Converted at the same 0.75pt
 * per pixel as the cell geometry, the PDF's lines weigh what the screen's do. Double is the exception:
 * the preview can only draw it as one thin line, and here it is Excel's two thin lines one pixel apart.
 */
type BorderStroke = { widthPx: number; dashPx: number[]; double: boolean };

const SOLID: number[] = [];
const HAIR_DASH_PX = [1, 2];
const DOTTED_DASH_PX = [2];
const DASHED_DASH_PX = [3];
const DASH_DOT_DASH_PX = [2, 5, 2];
const DASH_DOT_DOT_DASH_PX = [2, 2, 5, 2, 2];
const THIN_WIDTH_PX = 1;
const MEDIUM_WIDTH_PX = 2;
const THICK_WIDTH_PX = 3;

const BORDER_STROKES: Record<FortuneBorderStyleCode, BorderStroke> = {
	[FortuneBorderStyle.Thin]: { widthPx: THIN_WIDTH_PX, dashPx: SOLID, double: false },
	[FortuneBorderStyle.Hair]: { widthPx: THIN_WIDTH_PX, dashPx: HAIR_DASH_PX, double: false },
	[FortuneBorderStyle.Dotted]: { widthPx: THIN_WIDTH_PX, dashPx: DOTTED_DASH_PX, double: false },
	[FortuneBorderStyle.Dashed]: { widthPx: THIN_WIDTH_PX, dashPx: DASHED_DASH_PX, double: false },
	[FortuneBorderStyle.DashDot]: { widthPx: THIN_WIDTH_PX, dashPx: DASH_DOT_DASH_PX, double: false },
	[FortuneBorderStyle.DashDotDot]: { widthPx: THIN_WIDTH_PX, dashPx: DASH_DOT_DOT_DASH_PX, double: false },
	[FortuneBorderStyle.Double]: { widthPx: THIN_WIDTH_PX, dashPx: SOLID, double: true },
	[FortuneBorderStyle.Medium]: { widthPx: MEDIUM_WIDTH_PX, dashPx: SOLID, double: false },
	[FortuneBorderStyle.MediumDashed]: { widthPx: MEDIUM_WIDTH_PX, dashPx: DASHED_DASH_PX, double: false },
	[FortuneBorderStyle.MediumDashDot]: { widthPx: MEDIUM_WIDTH_PX, dashPx: DASH_DOT_DASH_PX, double: false },
	[FortuneBorderStyle.MediumDashDotDot]: { widthPx: MEDIUM_WIDTH_PX, dashPx: DASH_DOT_DOT_DASH_PX, double: false },
	// The preview draws it as a thin dash-dot; matched rather than made medium the way Excel draws it.
	[FortuneBorderStyle.SlantDashDot]: { widthPx: THIN_WIDTH_PX, dashPx: DASH_DOT_DASH_PX, double: false },
	[FortuneBorderStyle.Thick]: { widthPx: THICK_WIDTH_PX, dashPx: SOLID, double: false },
};

/** Excel's double border is a 3px band: a 1px line, a 1px gap and a 1px line, so each line's centre sits
 *  one pixel off the edge. */
const DOUBLE_LINE_OFFSET_PX = 1;

/** How much room a style's lines take across the edge. Heavier lines are stroked last so they end up on top. */
const footprintPx = (stroke: BorderStroke): number => (stroke.double ? stroke.widthPx + DOUBLE_LINE_OFFSET_PX * 2 : stroke.widthPx);

/** Float slack when joining the end of one segment to the start of the next, far below anything visible. */
const JOIN_TOLERANCE_PT = 0.01;
const KEY_PRECISION = 100;

/**
 * Join segments that continue one another along the same line, in the same style and colour, into one
 * stroke. That way a dash pattern runs unbroken across cell boundaries instead of restarting in every
 * cell, and an edge two neighbours both declare is stroked once.
 */
const joinCollinear = (segments: BorderSegment[]): BorderSegment[] => {
	const groups = new Map<string, BorderSegment[]>();
	for (const segment of segments) {
		const key = [segment.orientation, Math.round(segment.fixedPt * KEY_PRECISION), segment.side.style, segment.side.color.toUpperCase()].join("|");
		const group = groups.get(key);
		if (group) group.push(segment);
		else groups.set(key, [segment]);
	}

	const joined: BorderSegment[] = [];
	for (const group of groups.values()) {
		group.sort((a, b) => a.fromPt - b.fromPt);
		let current = { ...group[0] };
		for (const next of group.slice(1)) {
			if (next.fromPt <= current.toPt + JOIN_TOLERANCE_PT) current.toPt = Math.max(current.toPt, next.toPt);
			else {
				joined.push(current);
				current = { ...next };
			}
		}
		joined.push(current);
	}
	return joined;
};

/** jsPDF's projecting cap: each line runs half its width past its ends, so lines meeting at a corner close it. */
const SQUARE_LINE_CAP = "square";

const strokeSegment = (doc: jsPDF, segment: BorderSegment, scale: number): void => {
	const stroke = BORDER_STROKES[segment.side.style];
	const ptPerPx = PIXELS_TO_POINTS * scale;
	doc.setLineWidth(stroke.widthPx * ptPerPx);
	doc.setDrawColor(segment.side.color);
	doc.setLineDashPattern(
		stroke.dashPx.map((px) => px * ptPerPx),
		0,
	);
	const offsetsPt = stroke.double ? [-DOUBLE_LINE_OFFSET_PX * ptPerPx, DOUBLE_LINE_OFFSET_PX * ptPerPx] : [0];
	for (const offsetPt of offsetsPt) {
		const fixedPt = segment.fixedPt + offsetPt;
		if (segment.orientation === "horizontal") doc.line(segment.fromPt, fixedPt, segment.toPt, fixedPt);
		else doc.line(fixedPt, segment.fromPt, fixedPt, segment.toPt);
	}
};

/**
 * Stroke every collected border on the page it belongs to, then return to the page drawing was on.
 * Wrapped in a graphics-state save and restore, so the line width, colour, dash and cap set here reach
 * nothing drawn later on the page (the header and footer are stamped afterwards).
 */
export const drawCollectedBorders = (doc: jsPDF, collector: BorderCollector, scale: number): void => {
	if (collector.length === 0) return;
	const resumePage = doc.getCurrentPageInfo().pageNumber;
	const byPage = new Map<number, BorderSegment[]>();
	for (const segment of collector) {
		const pageSegments = byPage.get(segment.page);
		if (pageSegments) pageSegments.push(segment);
		else byPage.set(segment.page, [segment]);
	}

	for (const [page, segments] of byPage) {
		doc.setPage(page);
		doc.saveGraphicsState();
		doc.setLineCap(SQUARE_LINE_CAP);
		const ordered = joinCollinear(segments).sort((a, b) => footprintPx(BORDER_STROKES[a.side.style]) - footprintPx(BORDER_STROKES[b.side.style]));
		for (const segment of ordered) strokeSegment(doc, segment, scale);
		doc.restoreGraphicsState();
	}
	doc.setPage(resumePage);
};
